import { useEffect, useState } from 'react';
import { findLanguage, isVasteKamer, type Gender } from '@tolk/shared';
import RoomPage from './RoomPage';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { noteer } from '../lib/meetlog';
import { toegangMelding, vraagToegang, type ToegangProbleem } from '../lib/toegang';
import { aanspreekvormLabel, sfeerLabel, teksten } from '../teksten';
import { ApiFout, leesKamer, magOpnieuw, type KamerPubliek } from '../lib/api';
import Kader from '../components/Kader';
import MeetKnop from '../components/MeetKnop';
import { leesHervatting, onthoudHervatting } from '../lib/hervatten';

/**
 * De pagina waar de QR-code op uitkomt.
 *
 * Taalkeuze, stem, pincode en toegang tot de microfoon lopen allemaal hier
 * langs, want dit is het laatste scherm vóór het gesprek. Daarom staat de
 * microfoontest (/mic) hier ook: dit is het moment waarop hij nut heeft.
 */
export default function JoinPage({ kamerId }: { kamerId: string }) {
  // De taal die je kiest bepaalt de taal van de HELE app, niet alleen van de
  // vertaling. Tot je gekozen hebt is er nog geen keuze, dus Nederlands.
  const [gekozenTaal, setGekozenTaal] = useState<string | null>(null);
  const t = teksten(gekozenTaal ?? 'nl');
  const mijnTaal = gekozenTaal;
  const [kamer, setKamer] = useState<KamerPubliek | null>(null);
  const [fout, setFout] = useState<{ code: string; bericht: string } | null>(null);
  const [poging, setPoging] = useState(0);

  const [gender, setGender] = useState<Gender>('man');
  const [pin, setPin] = useState('');
  const [binnen, setBinnen] = useState(false);
  const [vraagt, setVraagt] = useState(false);
  const [toegangFout, setToegangFout] = useState<ToegangProbleem | null>(null);

  // De speler wordt HIER gemaakt, niet in de kamer. Alleen zo kan de klik op
  // "Ga de kamer in" hem ontgrendelen - en dat is de enige echte
  // gebruikersactie die we hebben.
  const speler = useAudioPlayer();

  useEffect(() => {
    let afgebroken = false;
    setFout(null);

    leesKamer(kamerId)
      .then((k) => {
        if (!afgebroken) setKamer(k);
      })
      .catch((error: unknown) => {
        if (afgebroken) return;
        setFout(
          error instanceof ApiFout
            ? { code: error.code, bericht: error.message }
            : { code: 'onbekend', bericht: 'Er ging iets mis.' },
        );
      });

    // Voorkomt dat een antwoord van een oude kamer nog binnenkomt nadat de
    // pagina is gewisseld.
    return () => {
      afgebroken = true;
    };
  }, [kamerId, poging]);

  /**
   * Kwam je hier al eerder binnen? Zet dan je taal en stem alvast klaar.
   *
   * NIET automatisch naar binnen. De knop vraagt in dezelfde klik de
   * microfoontoestemming en ontgrendelt het geluid, en dat mag alleen bij een
   * echte aanraking. Zonder die tik zou je in het gesprek zitten met het geluid
   * op slot - zichtbaar niets aan de hand, en toch stuk. Eén tik dus, geen nul.
   *
   * Bij een kamer met pincode gebeurt er niets: die code bewaren we bewust
   * nergens.
   */
  useEffect(() => {
    if (kamer === null || kamer.pinVereist || gekozenTaal !== null) return;
    const vorige = leesHervatting(kamer.id, [kamer.langA, kamer.langB]);
    if (vorige === null) return;
    setGekozenTaal(vorige.taal);
    setGender(vorige.gender);
  }, [kamer, gekozenTaal]);

  if (fout !== null) {
    // Een tijdelijke storing verdient een knop; een verlopen kamer niet, want
    // daar helpt opnieuw proberen nooit. Dezelfde melding voor allebei laat
    // mensen eindeloos verversen.
    const tijdelijk = magOpnieuw(fout.code);
    return (
      <Kader titel={tijdelijk ? t.serverReageertNiet : t.kamerWerktNiet}>
        <p className="text-sm text-slate-650 mb-4">{fout.bericht}</p>
        {tijdelijk ? (
          <button
            type="button"
            onClick={() => setPoging((n) => n + 1)}
            className="w-full py-2.5 rounded-xl bg-gio-blue hover:bg-gio-dark text-white
                       font-bold text-sm transition-colors duration-155"
          >
            {t.probeerOpnieuw}
          </button>
        ) : (
          <p className="text-xs text-slate-450">
            {t.vraagNieuweCode}
          </p>
        )}
      </Kader>
    );
  }

  if (kamer === null) {
    return (
      <Kader titel={t.kamerOphalen}>
        <div className="h-2 w-24 rounded-full bg-slate-150 overflow-hidden">
          <div className="h-full w-1/2 bg-gio-blue animate-pulse" />
        </div>
      </Kader>
    );
  }

  if (binnen && mijnTaal !== null) {
    return (
      <RoomPage
        kamer={kamer}
        pin={kamer.pinVereist ? pin : null}
        lang={mijnTaal}
        gender={gender}
        speler={speler}
        terug={() => setBinnen(false)}
      />
    );
  }

  const a = findLanguage(kamer.langA);
  const b = findLanguage(kamer.langB);
  const klaar = mijnTaal !== null && (!kamer.pinVereist || /^[0-9]{4}$/.test(pin));

  /**
   * Toestemming vragen en dan pas naar binnen. Allebei binnen deze klik, want
   * dat is de enige echte gebruikersactie.
   *
   * Een geweigerde microfoon houdt je NIET buiten: typen werkt gewoon. Alleen
   * de melding blijft staan zodat je weet waarom inspreken niet kan.
   */
  function kiesTaal(code: string): void {
    setGekozenTaal(code);
  }

  // Vastgepakt in een const: binnen een functie weet TypeScript niet meer dat
  // de nulcontrole hierboven al gedaan is.
  const dezeKamer = kamer;

  async function naarBinnen(): Promise<void> {
    setVraagt(true);
    noteer('knop: naar binnen');
    // Alleen chat: niet om de microfoon vragen. Een toestemmingsvraag voor
    // iets dat je niet gaat gebruiken maakt mensen terecht wantrouwig. Gaat de
    // kamer later toch naar spraak, dan vraagt de browser het bij de eerste
    // druk op inspreken, en het geluid heeft zijn eigen tik-hier-knop.
    const uitslag = dezeKamer.alleenChat
      ? { microfoon: false, geluid: false, probleem: null }
      : await vraagToegang(speler.ontgrendel);
    noteer(`toegang: microfoon=${uitslag.microfoon ? 'ja' : 'nee'} probleem=${uitslag.probleem ?? '-'}`);
    setVraagt(false);
    setToegangFout(uitslag.probleem);
    // Pas onthouden als je er ook echt in gaat. Een halve poging is geen keuze.
    // Niet bij een kamer met pincode: dan zou de volgende tik stranden op een
    // code die we niet bewaren.
    if (mijnTaal !== null && !dezeKamer.pinVereist) {
      onthoudHervatting(dezeKamer.id, { taal: mijnTaal, gender });
    }
    setBinnen(true);
  }
  const verlooptOm = new Date(kamer.expiresAt).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Kader
      titel={kamer.label === '' ? t.tolkgesprek : kamer.label}
      onderschrift={t.juisteKamer}
      taalcode={gekozenTaal ?? 'nl'}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-2">
        {t.welkeTaalSprekJij}
      </p>
      <div className="flex items-center justify-between gap-3 mb-5">
        <Taal
          vlag={a?.flag ?? '🏳️'}
          naam={a?.eigenNaam ?? kamer.langA}
          gekozen={mijnTaal === kamer.langA}
          kies={() => kiesTaal(kamer.langA)}
        />
        <Taal
          vlag={b?.flag ?? '🏳️'}
          naam={b?.eigenNaam ?? kamer.langB}
          gekozen={mijnTaal === kamer.langB}
          kies={() => kiesTaal(kamer.langB)}
        />
      </div>

      {/* Bij alleen chat klinkt er geen stem, dus ook niets om te kiezen. */}
      <div className={`mb-4 ${kamer.alleenChat ? 'hidden' : ''}`}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-2">
          {t.jouwStem}
        </p>
        <div className="flex gap-3">
          {(['man', 'vrouw'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGender(g)}
              className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-colors
                          duration-155 ${
                            gender === g
                              ? 'border-gio-blue bg-gio-blue/10 text-gio-dark'
                              : 'border-slate-250 bg-slate-50 text-slate-550'
                          }`}
            >
              {g === 'man' ? t.man : t.vrouw}
            </button>
          ))}
        </div>
      </div>

      {/*
        Alleen chat VOOR het binnenkomen melden: wie klaarstaat om te praten
        en dan geen microfoonknop vindt, denkt dat er iets stuk is. Bij alleen
        chat wordt er niets opgenomen, dus de melding over je stem vervalt.
      */}
      {kamer.alleenChat ? (
        <p className="text-xs text-slate-650 bg-gio-blue/8 border-2 border-gio-blue/25 rounded-xl px-3 py-2 mb-3">
          {t.alleenChatMelding}
        </p>
      ) : (
        kamer.stemDelen && (
          <p className="text-xs text-slate-650 bg-gio-blue/8 border-2 border-gio-blue/25 rounded-xl px-3 py-2 mb-3">
            {t.stemGaatMee}
          </p>
        )
      )}

      {kamer.pinVereist && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-2">
            {t.pincode}
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="0000"
            className="w-full p-3 bg-slate-50 border-2 border-slate-250 rounded-xl text-center
                       font-mono text-2xl tracking-[0.4em] text-gio-dark placeholder-slate-350
                       focus:outline-none focus:border-gio-orange focus:bg-white
                       transition-colors duration-155"
          />
          <p className="text-[11px] text-slate-450 mt-1.5">
            {t.pincodeUitleg}
          </p>
        </div>
      )}

      <dl className="text-xs text-slate-550 space-y-1 border-t-2 border-slate-150 pt-4">
        <div className="flex justify-between">
          <dt>{t.toon}</dt>
          <dd className="text-slate-650 font-semibold">
            {aanspreekvormLabel(kamer.aanspreekvorm, t)} · {sfeerLabel(kamer.sfeer, t)}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt>{t.verlooptOm}</dt>
          <dd className="font-mono text-slate-650">
            {isVasteKamer(kamer.expiresAt) ? t.nooitVasteKamer : verlooptOm}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt>{t.kamer}</dt>
          <dd className="font-mono text-slate-450">{kamer.id.slice(0, 8)}…</dd>
        </div>
      </dl>

      {toegangFout !== null && (
        <p className="mt-4 text-xs text-gio-danger bg-gio-danger/5 border-2 border-gio-danger/20
                      rounded-xl px-3 py-2.5 leading-relaxed">
          {toegangMelding(toegangFout, t)}
        </p>
      )}

      <button
        type="button"
        onClick={() => void naarBinnen()}
        disabled={!klaar || vraagt}
        className="w-full mt-5 py-3 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                   font-bold text-sm transition-colors duration-155
                   disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {vraagt
          ? t.microfoonAanvragen
          : mijnTaal === null
            ? t.kiesEerstJeTaal
            : toegangFout !== null
              ? t.probeerOpnieuw
              : t.gaDeKamerIn}
      </button>

      {!kamer.alleenChat && (
        <p className="mt-2 text-[11px] text-slate-450 leading-relaxed text-center">
          {t.toestemmingUitleg}
        </p>
      )}

      {/*
        De Meet-link van het gesprek, als die er is. Hier al, en niet pas in de
        kamer: wie via de link binnenkomt, wil vaak EERST het videogesprek in en
        dan pas tolken. In een nieuw tabblad, dus deze pagina blijft staan.
      */}
      {kamer.meetLink !== null && (
        <div className="mt-4">
          <MeetKnop link={kamer.meetLink} />
        </div>
      )}

      {/*
        In een NIEUW tabblad, met opzet. De test pakt de microfoon zelf, en op
        deze pagina staan dan al een taalkeuze en soms een pincode - een gewone
        link zou die weggooien en je bij terugkomst opnieuw laten invullen.

        De taal gaat mee in de link. /mic hangt buiten een kamer en heeft dus
        geen keuze om te volgen; zonder ?taal valt hij terug op de taal van de
        browser, en die klopt niet altijd met wat je hier aanwees.
      */}
      <p className={`mt-3 text-center ${kamer.alleenChat ? 'hidden' : ''}`}>
        <a
          href={mijnTaal !== null ? `/mic?taal=${encodeURIComponent(mijnTaal)}` : '/mic'}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-bold text-gio-blue hover:text-gio-dark underline
                     underline-offset-2 transition-colors duration-155"
        >
          {t.microfoonTestLink}
        </a>
      </p>
    </Kader>
  );
}

function Taal({
  vlag,
  naam,
  gekozen,
  kies,
}: {
  vlag: string;
  naam: string;
  gekozen: boolean;
  kies: () => void;
}) {
  return (
    <button
      type="button"
      onClick={kies}
      className={`flex-1 text-center border-2 rounded-xl py-3 transition-colors duration-155 ${
        gekozen
          ? 'border-gio-blue bg-gio-blue/10'
          : 'border-slate-250 bg-slate-50 hover:border-slate-350'
      }`}
    >
      <div className={`text-2xl leading-none mb-1 ${gekozen ? '' : 'opacity-50'}`}>{vlag}</div>
      <div className="text-xs font-bold text-gio-dark">{naam}</div>
    </button>
  );
}
